import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { unwrap } from '@truecare/api-client';
import { Alert, Badge, Card, type BadgeTone } from '@truecare/ui';
import { formatDate } from '../lib/format-date';
import type {
  CaregiverEarningsYear,
  CaregiverPayoutLineSelf,
  CaregiverPayoutSelfListItem,
} from '@truecare/schemas';
import { api } from '../api';
import { Pager, usePageClamp } from './ShiftListControls';

// Doc 17 Part B — same page size and pager as the shift lists. A weekly-paid
// care professional has ~53 payouts a year, which the old single page of 50
// silently cut off.
const PAYOUTS_PAGE_SIZE = 20;

/**
 * Phase 11d caregiver payouts tabs, flattened for the Phase-17 layout-C /me
 * redesign: PayoutsTab + EarningsTab are standalone exports consumed directly
 * as top-level tabs by <PersonaWorkspace> (the old CaregiverPayoutsPanel
 * wrapper + its inner TabButton were removed).
 *
 *  - **Payouts** — year-filtered list of ISSUED / PAID / VOIDED payouts. Click
 *    a row to expand the detail (line items + signed PDF link). DRAFT payouts
 *    are hidden per 11d-2.
 *  - **Earnings** — GROUP-BY-year totals per 11d-6.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Payouts tab
// ─────────────────────────────────────────────────────────────────────────────

export function PayoutsTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['me', 'payouts', year, page],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/me/caregiver/payouts', {
          params: { query: { year, page, pageSize: PAYOUTS_PAGE_SIZE } },
        }),
      ),
    // Paging keeps the current list (and the year picker) on screen while the
    // next page loads, instead of swapping the whole tab for "Loading…".
    placeholderData: keepPreviousData,
  });
  usePageClamp(page, setPage, PAYOUTS_PAGE_SIZE, listQuery.data?.totalCount, listQuery.isPlaceholderData);

  // Per-year confirmed-earnings totals (ISSUED + PAID; voids excluded), used for
  // the selected-year summary above the list.
  const earningsQuery = useQuery({
    queryKey: ['me', 'earnings'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/earnings')),
  });

  if (listQuery.isPending) {
    return <p className="font-body text-ink-muted">Loading payouts…</p>;
  }
  if (listQuery.isError) {
    return <Alert tone="danger" title="Couldn't load payouts">Refresh the page.</Alert>;
  }
  const items = listQuery.data.items;
  const yearSummary = earningsQuery.data?.years.find(
    (y: CaregiverEarningsYear) => y.year === year,
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <label className="font-body text-sm text-ink-muted" htmlFor="payout-year">
          Year
        </label>
        <select
          id="payout-year"
          value={year}
          onChange={(e) => {
            setYear(parseInt(e.target.value, 10));
            setPage(1);
            setExpandedId(null);
          }}
          className="rounded-md border border-border bg-paper px-3 py-1.5 font-body text-sm text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/30"
        >
          {yearOptions(currentYear).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* Selected-year summary — confirmed earnings for the chosen year. Gated on
          the earnings query so a failed/loading fetch doesn't render a misleading
          "$0.00" above populated payout rows. */}
      {earningsQuery.isError ? (
        <Alert tone="danger" title="Couldn't load your earnings total">
          Your payouts are shown below, but the {year} earnings summary is
          temporarily unavailable. Refresh the page to try again.
        </Alert>
      ) : (
        <Card padding="md">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="font-heading text-lg">
              {year} earnings{year === currentYear && ' (to date)'}
            </span>
            <span className="font-heading text-lg">
              {earningsQuery.isLoading ? '…' : formatCents(yearSummary?.totalCents ?? 0)}
            </span>
          </div>
          <p className="mt-1 font-body text-xs text-ink-muted">
            {earningsQuery.isLoading ? (
              'Loading your confirmed-earnings total…'
            ) : (
              <>
                Confirmed payouts only — subtotal{' '}
                {formatCents(yearSummary?.subtotalCents ?? 0)}
                {yearSummary && yearSummary.hstCents > 0
                  ? ` + HST ${formatCents(yearSummary.hstCents)}`
                  : ''}
                . Voided payouts aren't counted.
              </>
            )}
          </p>
        </Card>
      )}

      {items.length === 0 ? (
        <p className="font-body text-ink-muted">No payouts in {year}.</p>
      ) : (
        <>
          {items.map((p: CaregiverPayoutSelfListItem) => (
            <PayoutRow
              key={p.id}
              payout={p}
              expanded={expandedId === p.id}
              onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
            />
          ))}
          <Pager
            page={page}
            pageSize={PAYOUTS_PAGE_SIZE}
            totalCount={listQuery.data.totalCount}
            onPage={(p) => {
              setPage(p);
              setExpandedId(null);
            }}
            noun="payout"
          />
        </>
      )}
    </div>
  );
}

function PayoutRow({
  payout,
  expanded,
  onToggle,
}: {
  payout: CaregiverPayoutSelfListItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const detailId = `payout-detail-${payout.id}`;
  return (
    <Card padding="md" flat className="bg-paper-warm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={detailId}
        className="w-full text-left flex flex-wrap items-baseline gap-x-3 gap-y-1"
      >
        <strong className="font-heading text-lg">{payout.payoutNumber}</strong>
        <span className="font-body text-sm text-ink-muted">
          {formatDate(payout.cycleStartDate)} — {formatDate(payout.cycleEndDate)}
        </span>
        <span className="ml-auto font-heading text-lg">
          {formatCents(payout.totalCents)}
        </span>
        <PayoutStatusBadge status={payout.status} />
        {/* Persistent expand cue — the row toggles inline detail; without a
            chevron the affordance is invisible at rest and on touch. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className={`h-4 w-4 shrink-0 text-ink-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 7l5 5 5-5" />
        </svg>
      </button>
      {expanded && (
        <div id={detailId}>
          <PayoutDetail payoutId={payout.id} />
        </div>
      )}
    </Card>
  );
}

// Payout-specific labels ("In transit" for ISSUED reads clearer to a caregiver
// than the generic humanized "Issued"). Named distinctly so it never collides
// with the shared @truecare/ui StatusBadge import.
function PayoutStatusBadge({ status }: { status: 'ISSUED' | 'PAID' | 'VOIDED' }) {
  const map: Record<
    'ISSUED' | 'PAID' | 'VOIDED',
    { tone: BadgeTone; variant: 'soft' | 'solid'; label: string }
  > = {
    ISSUED: { tone: 'forest', variant: 'soft', label: 'In transit' },
    PAID: { tone: 'success', variant: 'solid', label: 'Paid' },
    VOIDED: { tone: 'neutral', variant: 'soft', label: 'Voided' },
  };
  const b = map[status];
  return (
    <Badge tone={b.tone} variant={b.variant}>
      {b.label}
    </Badge>
  );
}

function PayoutDetail({ payoutId }: { payoutId: string }) {
  const detail = useQuery({
    queryKey: ['me', 'payouts', 'detail', payoutId],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/me/caregiver/payouts/{id}', {
          params: { path: { id: payoutId } },
        }),
      ),
  });
  if (detail.isPending) {
    return <p className="font-body text-sm text-ink-muted mt-3">Loading details…</p>;
  }
  if (detail.isError) {
    return (
      <Alert tone="danger" title="Couldn't load detail" className="mt-3">
        Try again.
      </Alert>
    );
  }
  const p = detail.data;

  return (
    <div className="mt-4 pt-4 border-t border-ink-soft">
      <dl className="grid grid-cols-2 gap-y-1 text-sm font-body mb-4">
        <dt className="text-ink-muted">Subtotal</dt>
        <dd>{formatCents(p.subtotalCents)}</dd>
        {p.hstRegistrantAtIssue && (
          <>
            <dt className="text-ink-muted">HST (registrant #{p.hstNumberAtIssue})</dt>
            <dd>{formatCents(p.hstCents)}</dd>
          </>
        )}
        <dt className="text-ink-muted font-medium">Total</dt>
        <dd className="font-medium">{formatCents(p.totalCents)}</dd>
        <dt className="text-ink-muted">Issued</dt>
        <dd>{formatDate(p.issuedAt)}</dd>
        {p.status === 'ISSUED' && p.holdUntil !== null && (
          <>
            <dt className="text-ink-muted">Expected by</dt>
            <dd>
              {formatDate(p.holdUntil)}{' '}
              <span title="Funds in transit — bank EFT processing" className="text-ink-muted">
                ⓘ
              </span>
            </dd>
          </>
        )}
        {p.status === 'PAID' && p.paidAt !== null && (
          <>
            <dt className="text-ink-muted">Paid</dt>
            <dd>{formatDate(p.paidAt)}</dd>
          </>
        )}
        {p.status === 'VOIDED' && p.voidedAt !== null && (
          <>
            <dt className="text-ink-muted">Voided</dt>
            <dd>
              {formatDate(p.voidedAt)}
              {p.voidReason !== null && <> — {p.voidReason}</>}
            </dd>
          </>
        )}
      </dl>

      {p.lines.length > 0 && (
        <>
          <h4 className="font-heading text-base mb-2">Line items</h4>
          <table className="w-full text-sm font-body mb-4">
            <thead>
              <tr className="text-ink-muted text-xs uppercase">
                <th className="text-left pb-1">Date</th>
                <th className="text-left pb-1">Buyer</th>
                <th className="text-right pb-1">Hours</th>
                <th className="text-right pb-1">Rate</th>
                <th className="text-right pb-1">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {p.lines.map((l: CaregiverPayoutLineSelf) => (
                <tr key={l.id} className="border-t border-ink-soft">
                  <td className="py-1">{l.shiftDate ? formatDate(l.shiftDate) : '—'}</td>
                  <td className="py-1">
                    {l.buyerLabel ?? l.description}
                    {l.lineType === 'STAT_HOLIDAY_PAY' && (
                      <span className="ml-1 text-xs text-forest">(stat-holiday)</span>
                    )}
                    {l.lineType === 'CANCELLATION_PAY' && (
                      <span className="ml-1 text-xs text-ink-muted">(cancellation)</span>
                    )}
                    {l.lineType === 'BONUS' && (
                      <span className="ml-1 text-xs text-forest">(bonus)</span>
                    )}
                    {l.lineType === 'MANUAL_ADJUSTMENT' && (
                      <span className="ml-1 text-xs text-ink-muted">(adjustment)</span>
                    )}
                  </td>
                  <td className="py-1 text-right">{l.quantityHours}</td>
                  <td className="py-1 text-right">${l.unitPayRate}</td>
                  <td className="py-1 text-right">{formatCents(l.subtotalCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {p.statementPdfDownloadUrl !== null && (
        <a
          href={p.statementPdfDownloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-3 py-1.5 bg-forest text-paper rounded-md font-body text-sm"
        >
          Download statement (PDF)
        </a>
      )}

      <div className="mt-4 text-xs text-ink-muted font-body">
        <strong>Disagreement?</strong> Email operations at{' '}
        <a href="mailto:support@truecarepartners.ca" className="underline">
          support@truecarepartners.ca
        </a>{' '}
        with the payout number + your concern. We'll review against the
        timesheet + shift records and reply within 2 business days. If a
        correction is owed, we'll issue it to you separately by EFT or cheque
        and email you the details — it won't be folded into a future payout.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Earnings tab
// ─────────────────────────────────────────────────────────────────────────────

export function EarningsTab() {
  const earnings = useQuery({
    queryKey: ['me', 'earnings'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/earnings')),
  });
  if (earnings.isPending) {
    return <p className="font-body text-ink-muted">Loading earnings…</p>;
  }
  if (earnings.isError) {
    return <Alert tone="danger" title="Couldn't load earnings">Refresh the page.</Alert>;
  }
  const years = earnings.data.years;
  if (years.length === 0) {
    return <p className="font-body text-ink-muted">No payouts.</p>;
  }
  const currentYear = new Date().getFullYear();
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted font-body">
        Confirmed payouts only (ISSUED + PAID). Voided payouts are not counted.
      </p>
      {years.map((y: CaregiverEarningsYear) => (
        <Card key={y.year} padding="md" flat className="bg-paper-warm">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <strong className="font-heading text-lg">
              {y.year}
              {y.year === currentYear && (
                <span className="ml-2 text-xs text-ink-muted">(to date)</span>
              )}
            </strong>
            <span className="ml-auto font-heading text-lg">{formatCents(y.totalCents)}</span>
          </div>
          <dl className="grid grid-cols-2 gap-y-0.5 text-sm font-body text-ink-muted mt-2">
            <dt>Subtotal</dt>
            <dd className="text-right">{formatCents(y.subtotalCents)}</dd>
            {y.hstCents > 0 && (
              <>
                <dt>HST</dt>
                <dd className="text-right">{formatCents(y.hstCents)}</dd>
              </>
            )}
          </dl>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(dollars);
}


/** Year options shown in the year picker: current year + 4 prior. */
function yearOptions(currentYear: number): number[] {
  return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];
}
